import type { GetSalesOrderResponse } from '@erp/rpc';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ShoppingCartIcon,
  BuildingsIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  TruckIcon,
} from '@phosphor-icons/react';
import { formatDateOnlyString } from '@/utils/dateUtils';

// Helper function to format currency
const formatCurrencyValue = (
  value: string | undefined | null,
  currency = 'USD',
): string => {
  if (!value) return 'N/A';

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
};

function getStatusVariant(
  status: string,
): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (status) {
    case 'pending':
      return 'secondary';
    case 'in_progress':
      return 'default';
    case 'fulfilled':
      return 'outline';
    case 'closed':
      return 'outline';
    default:
      return 'secondary';
  }
}

function formatStatus(status: string): string {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

type SalesOrderWithDetails = NonNullable<GetSalesOrderResponse['salesOrder']>;

interface SalesOrderMetadataProps {
  salesOrder: SalesOrderWithDetails;
}

export function SalesOrderMetadata({ salesOrder }: SalesOrderMetadataProps) {
  const displayNumber =
    salesOrder.customerPurchaseOrderNumber || `SO-${salesOrder.internalId}`;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShoppingCartIcon className="h-4 w-4" />
            {displayNumber}
          </CardTitle>
          <Badge variant={getStatusVariant(salesOrder.status)}>
            {formatStatus(salesOrder.status)}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Main Sales Order Details */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <BuildingsIcon className="h-3 w-3" />
              Customer
            </div>
            <div className="text-sm font-medium">
              {salesOrder.customer?.name || 'Unknown Customer'}
            </div>
            {salesOrder.customer?.customerNumber && (
              <div className="text-xs text-muted-foreground">
                #{salesOrder.customer.customerNumber}
              </div>
            )}
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CurrencyDollarIcon className="h-3 w-3" />
              Total Amount
            </div>
            <div className="text-lg font-bold text-green-600">
              {formatCurrencyValue(
                salesOrder.totalAmount,
                salesOrder.currencyCode || 'USD',
              )}
            </div>
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              Order Date
            </div>
            <div className="font-medium text-sm">
              {formatDateOnlyString(salesOrder.orderDate)}
            </div>
          </div>

          {salesOrder.deliveryDate && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CalendarIcon className="h-3 w-3" />
                Delivery Date
              </div>
              <div className="font-medium text-sm">
                {formatDateOnlyString(salesOrder.deliveryDate)}
              </div>
            </div>
          )}

          {salesOrder.shipDate && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CalendarIcon className="h-3 w-3" />
                Ship Date
              </div>
              <div className="font-medium text-sm">
                {formatDateOnlyString(salesOrder.shipDate)}
              </div>
            </div>
          )}

          {salesOrder.customerPurchaseOrderNumber && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <ShoppingCartIcon className="h-3 w-3" />
                Customer PO #
              </div>
              <div className="font-medium text-sm">
                {salesOrder.customerPurchaseOrderNumber}
              </div>
            </div>
          )}
        </div>

        {/* Shipment Info */}
        {salesOrder.shipment && (
          <div className="border-t pt-3 space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <TruckIcon className="h-3 w-3" />
              Linked Shipment
            </div>
            <div className="text-sm">
              <span className="font-medium">
                {salesOrder.shipment.originLoadNumber ||
                  salesOrder.shipment.id.slice(0, 8)}
              </span>
              {salesOrder.shipment.origin &&
                salesOrder.shipment.destination && (
                  <span className="text-muted-foreground ml-2">
                    {salesOrder.shipment.origin} →{' '}
                    {salesOrder.shipment.destination}
                  </span>
                )}
              {salesOrder.shipment.status && (
                <Badge variant="outline" className="ml-2 text-xs">
                  {formatStatus(salesOrder.shipment.status)}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Source Info */}
        <div className="border-t pt-3">
          <div className="text-xs text-muted-foreground">
            Source: {salesOrder.source}
            {salesOrder.originSystem && (
              <span> | Origin: {salesOrder.originSystem}</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
